import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Trophy, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import AvatarUploader from "@/components/AvatarUploader";

/**
 * Signup Redesign · Step 1 of 2 — Essential Profile (revised for the
 * Customer-Experience Gate).
 *
 * Field list (all optional except display name):
 *   • Display name (required)
 *   • Favorite Club Team  — plain free text
 *   • Favorite National Team — plain free text (was a dropdown, now
 *     unrestricted per user feedback so collectors can enter any team)
 *   • Collecting Since — fixed dropdown (approved enum, see below)
 *   • City, State/Region, Country
 *   • Show my city and state publicly — separate visibility toggle
 *   • Profile photo (optional)
 *
 * NOTE ON LEGACY DATA: existing collectors saved as integer years
 * (`collecting_since=2010`) still render correctly because the
 * dropdown lists every approved year as a value; unknown values
 * are ignored so the collector can just pick one on next visit.
 */

// ─── Approved collecting-since options (do NOT change without owner
//    sign-off). Values sent to the backend are string codes that
//    persist stably even when we tweak labels later.
const COLLECTING_SINCE_OPTIONS = [
  { value: "",                    label: "Prefer not to say" },
  { value: "just_started_2026",   label: "Just started — 2026" },
  { value: "since_2022",          label: "Since 2022" },
  { value: "since_2018",          label: "Since 2018" },
  { value: "since_2014",          label: "Since 2014" },
  { value: "since_2010",          label: "Since 2010" },
  { value: "since_2006",          label: "Since 2006" },
  { value: "since_2002",          label: "Since 2002" },
  { value: "since_1998",          label: "Since 1998" },
  { value: "since_1994",          label: "Since 1994" },
  { value: "since_1990",          label: "Since 1990" },
  { value: "since_1986",          label: "Since 1986" },
  { value: "since_1982",          label: "Since 1982" },
  { value: "since_1978",          label: "Since 1978" },
  { value: "since_1974",          label: "Since 1974" },
  { value: "since_1970",          label: "Since 1970" },
  { value: "not_sure",            label: "Not sure" },
];

const COUNTRY_OPTIONS = [
  "United States", "Lebanon", "Canada", "United Kingdom", "France", "Germany",
  "Italy", "Spain", "Portugal", "Brazil", "Argentina", "Mexico", "Colombia",
  "Chile", "Peru", "Uruguay", "Netherlands", "Belgium", "Sweden", "Norway",
  "Denmark", "Finland", "Poland", "Turkey", "Greece", "Croatia", "Serbia",
  "United Arab Emirates", "Saudi Arabia", "Qatar", "Egypt", "Morocco",
  "Nigeria", "South Africa", "Kenya", "Japan", "South Korea", "China",
  "India", "Australia", "New Zealand", "Other",
];

/**
 * Legacy compatibility: some previously saved `collecting_since`
 * values were integer years (2010, 2022, etc.). Map them to the new
 * enum codes so the dropdown pre-selects correctly.
 */
function normalizeInitialSince(raw) {
  if (!raw) return "";
  const s = String(raw);
  if (COLLECTING_SINCE_OPTIONS.some((o) => o.value === s)) return s;
  const n = parseInt(s, 10);
  if (!Number.isNaN(n)) {
    if (n >= 2026) return "just_started_2026";
    if (n >= 2022) return "since_2022";
    if (n >= 2018) return "since_2018";
    if (n >= 2014) return "since_2014";
    if (n >= 2010) return "since_2010";
    if (n >= 2006) return "since_2006";
    if (n >= 2002) return "since_2002";
    if (n >= 1998) return "since_1998";
    if (n >= 1994) return "since_1994";
    if (n >= 1990) return "since_1990";
    if (n >= 1986) return "since_1986";
    if (n >= 1982) return "since_1982";
    if (n >= 1978) return "since_1978";
    if (n >= 1974) return "since_1974";
    if (n >= 1970) return "since_1970";
  }
  return "";
}

export default function OnboardingProfile() {
  const navigate = useNavigate();
  const { collector, refresh } = useCollectorAuth();

  const ob = collector?.onboarding || {};
  const isClaim = ob.entry_type === "certificate_claim";
  const certNumber = ob.entry_cert_number;

  const [displayName, setDisplayName] = useState(collector?.display_name || "");
  const [clubTeam, setClubTeam] = useState(collector?.favorite_club_team || "");
  const [nationalTeam, setNationalTeam] = useState(collector?.favorite_national_team || "");
  const [since, setSince] = useState(normalizeInitialSince(collector?.collecting_since));
  const [city, setCity] = useState(collector?.profile_city || collector?.city || "");
  const [state, setState] = useState(collector?.profile_state || collector?.state || "");
  const [country, setCountry] = useState(collector?.profile_country_name || "");
  const [showLocation, setShowLocation] = useState(
    Boolean(collector?.show_location_publicly)
  );
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  // C3.1 — per-field validation messages for the required location trio.
  const [errors, setErrors] = useState({ city: "", state: "", country: "" });

  useEffect(() => {
    if (!collector?.avatar_media_id) return;
    setAvatarUrl(`${process.env.REACT_APP_BACKEND_URL}/api/me/profile/avatar?v=${collector.avatar_updated_at || ""}`);
  }, [collector?.avatar_media_id, collector?.avatar_updated_at]);

  const save = async () => {
    if (!displayName.trim()) {
      toast.error("Please add a display name.");
      return;
    }
    // C3.1 — City / State / Country are REQUIRED during new-collector
    // onboarding. Whitespace-only values are rejected.
    const cityT = city.trim();
    const stateT = state.trim();
    const countryT = country.trim();
    const nextErrors = {
      city: cityT ? "" : "City is required.",
      state: stateT ? "" : "State is required.",
      country: countryT ? "" : "Country is required.",
    };
    setErrors(nextErrors);
    if (nextErrors.city || nextErrors.state || nextErrors.country) {
      toast.error("Please fill in your city, state, and country to continue.");
      return;
    }
    setBusy(true);
    try {
      const patch = {
        display_name: displayName.trim(),
        favorite_national_team: nationalTeam.trim() || null,
        favorite_club_team: clubTeam.trim() || null,
        profile_city: cityT,
        profile_state: stateT,
        profile_country_name: countryT,
        show_location_publicly: showLocation,
      };
      // `since` is now a string code (or ""). Empty means "prefer not
      // to say" → send 0 which the backend treats as clear-sentinel.
      patch.collecting_since = since || 0;
      await api.patch("/me/profile", patch);
      await api.post("/me/onboarding/step", { step: "albums" });
      await refresh();
      navigate("/onboarding/albums");
    } catch (e) {
      toast.error(formatApiError(e.response?.data) || "Could not save profile.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="onboarding-profile">
      <div className="flex items-center gap-2 mb-3">
        <div className="kicker kicker-yellow">Step 1 of 2</div>
        {isClaim && (
          <div
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--ink)] bg-white px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest"
            data-testid="onboarding-profile-claim-badge"
          >
            <Trophy size={11} className="text-[var(--panini-red)]" />
            Certificate {certNumber || "claim"}
          </div>
        )}
      </div>
      <h1 className="font-serif text-3xl sm:text-4xl mt-2" data-testid="onboarding-profile-headline">
        {isClaim
          ? "Almost there — set up your collector profile."
          : "Let's set up your collector profile."}
      </h1>
      <p className="mt-3 text-[var(--smoke)] max-w-2xl">
        Two quick steps and you&apos;re done. This is what other collectors see
        when they visit your public page. You can edit any of this later.
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 items-start">
        {/* Avatar */}
        <div>
          <div className="kicker kicker-black text-[11px] mb-3">Profile photo</div>
          <AvatarUploader
            currentUrl={avatarUrl}
            testidPrefix="onboarding-avatar"
            onUploaded={(_id, url) => setAvatarUrl(url)}
            onRemoved={() => setAvatarUrl(null)}
          />
          <p className="text-[11px] text-[var(--smoke)] mt-3">
            Optional · square crop · saved as 512×512 WebP.
          </p>
        </div>

        {/* Fields */}
        <div className="space-y-5">
          <label className="block">
            <div className="kicker kicker-black text-[11px] mb-2">Display name</div>
            <input
              data-testid="onboarding-profile-display-name"
              className="gm-field w-full"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Christian Ronaldo"
            />
          </label>

          <label className="block">
            <div className="kicker kicker-black text-[11px] mb-2">
              What is your favorite club team? <span className="text-[var(--smoke)] normal-case tracking-normal font-normal">(optional)</span>
            </div>
            <input
              data-testid="onboarding-profile-club-team"
              className="gm-field w-full"
              value={clubTeam}
              onChange={(e) => setClubTeam(e.target.value)}
              maxLength={80}
              placeholder="e.g. Al Ahli, Barcelona, Boca Juniors…"
            />
          </label>

          <label className="block">
            <div className="kicker kicker-black text-[11px] mb-2">
              What is your favorite national team? <span className="text-[var(--smoke)] normal-case tracking-normal font-normal">(optional)</span>
            </div>
            <input
              data-testid="onboarding-profile-national-team"
              className="gm-field w-full"
              value={nationalTeam}
              onChange={(e) => setNationalTeam(e.target.value)}
              maxLength={80}
              placeholder="e.g. Brazil, Argentina, Morocco…"
            />
          </label>

          <label className="block">
            <div className="kicker kicker-black text-[11px] mb-2">
              How long have you been collecting? <span className="text-[var(--smoke)] normal-case tracking-normal font-normal">(optional)</span>
            </div>
            <select
              data-testid="onboarding-profile-collecting-since"
              className="gm-field w-full"
              value={since}
              onChange={(e) => setSince(e.target.value)}
            >
              {COLLECTING_SINCE_OPTIONS.map((o) => (
                <option key={o.value || "prefer_not_to_say"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {/* Location fields — City / State / Country. C3.1: all three
              are REQUIRED during new-collector onboarding. Inline
              validation messages appear beneath each empty field on
              submit. The public-visibility toggle remains INDEPENDENT
              of the master profile_public switch. */}
          <div className="pt-2 border-t border-[var(--ink)]/10">
            <div className="kicker kicker-black text-[11px] mt-4 mb-2">
              Location <span className="text-[var(--panini-red)]" aria-hidden>*</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <input
                  data-testid="onboarding-profile-city"
                  className={"gm-field w-full " + (errors.city ? "border-[var(--panini-red)]" : "")}
                  value={city}
                  onChange={(e) => { setCity(e.target.value); if (errors.city) setErrors((p) => ({ ...p, city: "" })); }}
                  maxLength={80}
                  placeholder="City *"
                  aria-invalid={!!errors.city}
                  aria-describedby={errors.city ? "err-city" : undefined}
                  required
                />
                {errors.city && (
                  <p id="err-city" className="mt-1 text-xs text-[var(--panini-red)]" data-testid="onboarding-profile-city-error">
                    {errors.city}
                  </p>
                )}
              </div>
              <div>
                <input
                  data-testid="onboarding-profile-state"
                  className={"gm-field w-full " + (errors.state ? "border-[var(--panini-red)]" : "")}
                  value={state}
                  onChange={(e) => { setState(e.target.value); if (errors.state) setErrors((p) => ({ ...p, state: "" })); }}
                  maxLength={80}
                  placeholder="State or region *"
                  aria-invalid={!!errors.state}
                  aria-describedby={errors.state ? "err-state" : undefined}
                  required
                />
                {errors.state && (
                  <p id="err-state" className="mt-1 text-xs text-[var(--panini-red)]" data-testid="onboarding-profile-state-error">
                    {errors.state}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <select
                  data-testid="onboarding-profile-country"
                  className={"gm-field w-full " + (errors.country ? "border-[var(--panini-red)]" : "")}
                  value={country}
                  onChange={(e) => { setCountry(e.target.value); if (errors.country) setErrors((p) => ({ ...p, country: "" })); }}
                  aria-invalid={!!errors.country}
                  aria-describedby={errors.country ? "err-country" : undefined}
                  required
                >
                  <option value="">Country *</option>
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {errors.country && (
                  <p id="err-country" className="mt-1 text-xs text-[var(--panini-red)]" data-testid="onboarding-profile-country-error">
                    {errors.country}
                  </p>
                )}
              </div>
            </div>
            <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-sm text-[var(--ink)]">
              <input
                type="checkbox"
                data-testid="onboarding-profile-show-location"
                className="w-4 h-4 accent-[var(--panini-red)]"
                checked={showLocation}
                onChange={(e) => setShowLocation(e.target.checked)}
              />
              Show my city and state publicly
            </label>
            <p className="text-[11px] text-[var(--smoke)] mt-1">
              We keep this off by default. Your profile can be public without exposing your location.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="text-xs text-[var(--smoke)] flex items-center gap-2 justify-center sm:justify-start">
          <Sparkles size={13} className="text-[var(--panini-red)]" />
          Display name and location are required. Everything else is optional.
        </div>
        <button
          data-testid="onboarding-profile-continue"
          onClick={save}
          disabled={busy}
          className="btn-red inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? "Saving…" : "Continue to Albums"}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
